import { createClient } from '@/shared/api';
import UPDATE_STUDY_MUTATION from '@/entities/study/api/mutations/updateStudy';
import type { OnBoarding } from '@/entities/study/api/fragments/onBoardingFragment';
import type { StudySetting } from '@/entities/study/api/fragments/studySettingFragment';
import type { Patient, Insurance, Diagnosis } from '@/shared/api';
import type {
  UpdateStudyMutation,
  UpdateStudyMutationVariables,
} from '@/shared/api/generated/graphql';

type PhysicianContact = { phone1: string; nightPhone: string; fax: string };
type Physician = {
  id: string;
  firstName: string;
  lastName: string;
  contact: PhysicianContact;
  email: string;
};

export type UpdatedStudy = {
  id: string;
  friendlyId: string;
  referenceCode: string;
  isBiofluxDirect: boolean;
  lowCellSignal: boolean;
  rma: string;
  onboarding: OnBoarding;
  info: {
    insurance: Insurance;
    patient: Patient;
    interprettingPhysician: Physician;
    referringPhysician: Physician;
    diagnosis: Diagnosis;
    followOnStudy: { duration: number; studyType: string; reportDueDate: string };
  };
  studyHistory: { time: string; batteryLevel: number; status: string }[];
  deviceNotifications: { time: string; batteryLevel: number; status: string }[];
  reports: {
    id: string;
    reportId: string;
    type: string;
    createdAt: string;
    start: string;
    stop: string;
    status: string;
    event: { friendlyId: string; id: string };
    technicianComments: string;
    physicianInterpretation: string;
    isNotificationReport: boolean;
    lastModified: string;
    inbox: { date: string; priority: string };
    isArtifactReport: boolean;
  }[];
  start: string;
  stop: string;
  studyType: string;
  status: string;
  reportDueDate: string;
  studyNote: string;
  timezone: string;
  initDuration: number;
  linkedStudies: {
    id: string;
    friendlyId: string;
    initDuration: number;
    studyType: string;
    reportDueDate: string;
  }[];
  studySetting: StudySetting;
  facility: {
    id: string;
    name: string;
    contact: { country: string };
    arrears: any;
  };
  device: {
    id: string;
    deviceId: string;
    status: string;
    model: string;
    lastSync: { operator: string; gprs: any; schema: any };
  };
};

export type UpdateStudyResult = {
  isSuccess: boolean;
  message: string;
  study: UpdatedStudy;
};

// TODO(ts): typed as the UpdateStudyInput GraphQL input once its server-side shape is documented
/**
 * Applies a partial update to a study record. Runs the updateStudy mutation with the given props, throws the server message on failure, and returns the updated study.
 *
 * @param studyId - Identifier of the study to update.
 * @param props - Fields to change on the study.
 * @returns A promise resolving to the updated study.
 */
const handleUpdateStudy = async (studyId: string, props: any) => {
  const client = await createClient();
  const result = await client.mutate<UpdateStudyMutation, UpdateStudyMutationVariables>({
    mutation: UPDATE_STUDY_MUTATION,
    variables: {
      studyId,
      props,
    },
  });
  if (!result?.data.updateStudy?.isSuccess) {
    throw new Error(result?.data.updateStudy?.message);
  }
  return result?.data?.updateStudy?.study;
};

export default handleUpdateStudy;
